import { useEffect, useState } from 'react';
import { Wallet, CreditCard, ExternalLink, RadioTower, History } from 'lucide-react';
import { billingApi, ApiError } from '../../lib/api';
import type { BalanceResponse, SubscriptionResponse, UsageEvent } from '../../lib/types';
import { Spinner, EmptyState, ErrorBanner, DisclosureBanner } from '../../components/Misc';
import { formatDateTime, formatEur, titleCase } from '../../lib/format';

const STATUS_TONE: Record<string, string> = {
  SUCCESS: 'clear',
  SENT: 'advisory',
  PENDING: 'advisory',
  FAILED: 'critical',
  SKIPPED_NO_CONFIG: 'neutral',
};

export default function BillingPage() {
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [events, setEvents] = useState<UsageEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [sub, bal, ev] = await Promise.all([billingApi.subscription(), billingApi.balance(), billingApi.usageEvents(100)]);
        setSubscription(sub);
        setBalance(bal);
        setEvents(ev.events);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load billing information.');
      }
    })();
  }, []);

  return (
    <div className="stack gap-5">
      <div>
        <h1 className="h2-section">Billing &amp; usage</h1>
        <p className="body-text text-secondary">Your Sensifai Marketplace subscription, wallet balance, and metered usage history.</p>
      </div>

      {error && <ErrorBanner message={error} />}

      {!subscription || !balance ? (
        <Spinner label="Loading billing information…" />
      ) : (
        <div className="grid-2">
          <div className="card stack gap-3">
            <div className="row gap-2"><CreditCard size={18} color="var(--accent)" /><span className="h3" style={{ fontSize: '1.0625rem' }}>Subscription</span></div>
            {subscription.linked ? (
              <>
                <div className="row gap-2">
                  <span className="pill">{subscription.subscription?.status ?? 'unknown'}</span>
                  {subscription.live === false && <span className="pill" style={{ background: 'var(--status-advisory-bg)', color: 'var(--status-advisory)' }}>Cached</span>}
                </div>
                {subscription.subscription?.price_model || subscription.subscription?.priceModel ? (
                  <pre className="mono body-small" style={{ background: 'var(--surface-sunken)', padding: 12, borderRadius: 8, overflowX: 'auto', margin: 0 }}>
                    {JSON.stringify(subscription.subscription.price_model ?? subscription.subscription.priceModel, null, 2)}
                  </pre>
                ) : null}
                {subscription.cachedAt && <span className="body-small text-tertiary">Last refreshed {formatDateTime(subscription.cachedAt)}</span>}
                {subscription.warning && <DisclosureBanner>{subscription.warning}</DisclosureBanner>}
              </>
            ) : (
              <>
                <p className="body-small text-tertiary">This organization is not yet linked to a Sensifai Marketplace subscription.</p>
                {subscription.marketplaceUrl && (
                  <a className="btn btn-primary" href={subscription.marketplaceUrl} target="_blank" rel="noreferrer" style={{ width: 'fit-content' }}>
                    Link on Sensifai Marketplace <ExternalLink size={14} />
                  </a>
                )}
              </>
            )}
          </div>

          <div className="card stack gap-3">
            <div className="row gap-2"><Wallet size={18} color="var(--accent)" /><span className="h3" style={{ fontSize: '1.0625rem' }}>Wallet balance</span></div>
            {balance.linked ? (
              <>
                <div className="mono" style={{ fontSize: '2rem', fontWeight: 700 }}>{formatEur(balance.balanceEur)}</div>
                <div className="row gap-2">
                  {balance.live === false ? (
                    <span className="pill" style={{ background: 'var(--status-advisory-bg)', color: 'var(--status-advisory)' }}>
                      <RadioTower size={12} /> Cached{balance.cachedAt ? ` from ${formatDateTime(balance.cachedAt)}` : ''}
                    </span>
                  ) : (
                    <span className="pill" style={{ background: 'var(--status-clear-bg)', color: 'var(--status-clear)' }}>Live from Gatekeeper</span>
                  )}
                </div>
                {balance.warning && <DisclosureBanner>{balance.warning}</DisclosureBanner>}
              </>
            ) : (
              <p className="body-small text-tertiary">No wallet balance available until this organization is linked to a Marketplace subscription.</p>
            )}
          </div>
        </div>
      )}

      <div className="stack gap-3">
        <div className="row gap-2"><History size={18} /><span className="h3">Usage events</span></div>
        {events === null ? (
          <Spinner label="Loading usage events…" />
        ) : events.length === 0 ? (
          <EmptyState icon={<History size={32} />} title="No usage recorded yet" description="Metered actions (evaluations, AI extractions) will be reported here as they occur." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Occurred</th><th>Kind</th><th>Qty</th><th>Report status</th><th>Reported</th></tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.identifier}>
                    <td data-label="Occurred">{formatDateTime(e.occurredAt)}</td>
                    <td data-label="Kind">{titleCase(e.kind)}</td>
                    <td data-label="Qty" className="mono">{e.quantity}</td>
                    <td data-label="Report status">
                      <span className={`status-badge ${STATUS_TONE[e.reportStatus] ?? 'neutral'}`}>{e.reportStatus.replace(/_/g, ' ')}</span>
                      {e.failReason && <div className="body-small text-tertiary" style={{ marginTop: 4 }}>{e.failReason}</div>}
                    </td>
                    <td data-label="Reported">{e.reportedAt ? formatDateTime(e.reportedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
